import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Factory } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STATUS = { not_started: "Not started", wip: "In progress", blocked: "Blocked", hold: "On hold", qc_pending: "Waiting for QC", done: "Done", cancelled: "Cancelled" };
const OK = { start: "Started", done: "Marked done", note: "Note added" };
const BIG = "h-11 px-4 text-sm flex-1 min-w-[110px]";

export const useMySteps = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/my-steps").then((r) => setD(r.data)).catch(() => setD({ items: [], recent: [], counts: {} })), []);
  useEffect(() => { load(); }, [load]);
  return [d, load];
};

export const TextDialog = ({ title, hint, label, onClose, onSave, tid = "text-dialog" }) => {
  const [v, setV] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card" data-testid={tid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
        <textarea data-testid={`${tid}-input`} value={v} onChange={(e) => setV(e.target.value)} placeholder={label} className="w-full bg-muted/60 border p-2 text-sm h-24" />
        <button data-testid={`${tid}-save`} disabled={v.trim().length < 3} onClick={() => onSave(v.trim())} className="bg-primary text-primary-foreground h-10 px-4 text-sm disabled:opacity-50">Save</button>
      </DialogContent>
    </Dialog>
  );
};

const due = (x) => x.baseline_end || x.planned_end;

const Buttons = ({ x, send, setDlg }) => (
  <div className="flex flex-wrap gap-2 mt-3">
    {x.status === "not_started" && (x.ready || !x.waiting_for?.length
      ? <button data-testid={`step-start-${x.process_id}`} onClick={() => send("start")} className={`bg-primary text-primary-foreground ${BIG}`}>Start</button>
      : <button disabled data-testid={`step-waiting-${x.process_id}`} className={`border text-muted-foreground ${BIG}`}>Waiting for {x.waiting_for.join(", ")}</button>)}
    {x.status === "wip" && <button data-testid={`step-done-${x.process_id}`} onClick={() => send("done")} className={`bg-primary text-primary-foreground ${BIG}`}>Mark done</button>}
    {["not_started", "wip", "blocked", "hold", "qc_pending"].includes(x.status) && <button data-testid={`step-note-${x.process_id}`} onClick={() => setDlg("note")} className={`border ${BIG}`}>Add a note</button>}
    {x.undo_ok && <button data-testid={`step-undo-${x.process_id}`} onClick={() => setDlg("undo")} className={`border ${BIG}`}>Undo</button>}
  </div>
);

const StepCard = ({ x, onChanged, hi, today }) => {
  const [dlg, setDlg] = useState(null);
  const fail = (e) => { toast.error(errMsg(e)); onChanged(); };
  const send = (action, note = "") => api.post(`/my-steps/${x.process_id}/action`, { action, version: x.version, note }).then(() => { setDlg(null); toast.success(OK[action]); onChanged(); }).catch(fail);
  const undo = (reason) => api.post(`/my-steps/${x.process_id}/undo`, { version: x.version, reason }).then(() => { setDlg(null); toast.success("Undone"); onChanged(); }).catch(fail);
  const d = due(x);
  return (
    <div id={`my-step-${x.process_id}`} className={`ax-panel p-4 ${hi ? "ring-2 ring-primary" : ""}`} data-testid={`my-step-${x.process_id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 inline-flex items-center gap-1" data-testid={`my-step-tag-${x.process_id}`}><Factory className="h-3 w-3" />Production step</span>
        <span className="font-mono">{x.uid}</span><span className="text-muted-foreground truncate">{x.client_name}</span>
      </div>
      <div className="font-semibold mt-1">{x.name}</div>
      <div className="text-xs mt-1 flex flex-wrap gap-x-3">
        <span data-testid={`my-step-status-${x.process_id}`}>{STATUS[x.status]}</span>
        <span className={x.late ? "text-rose-600 font-medium" : d === today ? "text-primary" : "text-muted-foreground"}>{x.late ? "Late · " : ""}Due {d || "—"}</span>
        {x.dept_label && <span className="text-muted-foreground">{x.dept_label}</span>}
        {x.qc_required && x.status !== "qc_pending" && <span className="text-violet-700">Goes to QC when done</span>}
      </div>
      <Buttons x={x} send={send} setDlg={setDlg} />
      {dlg === "note" && <TextDialog tid="step-note-dialog" title="Add a note" label="What should production know?" onClose={() => setDlg(null)} onSave={(n) => send("note", n)} />}
      {dlg === "undo" && <TextDialog tid="step-undo-dialog" title="Undo the last update?" hint={`This takes back the last update on ${x.name}. Your reason is kept in the history.`} label="Why (required)" onClose={() => setDlg(null)} onSave={undo} />}
    </div>
  );
};

export const MySteps = ({ title = "Production steps given to you", data, reload }) => {
  const [own, ownLoad] = useMySteps();
  const [sp] = useSearchParams();
  const d = data || own;
  const load = reload || ownLoad;
  const hi = sp.get("step");
  useEffect(() => { if (hi && d) document.getElementById(`my-step-${hi}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [hi, d]);
  if (!d || (!d.items.length && !d.recent?.length)) return null;
  const today = new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
  return (
    <div className="mb-6" data-testid="my-steps">
      <div className="ax-label mb-2">{title} <span className="num text-muted-foreground">{d.items.length}</span></div>
      <div className="grid gap-3 md:grid-cols-2">{d.items.map((x) => <StepCard key={x.process_id} x={x} onChanged={load} hi={hi === x.process_id} today={today} />)}</div>
      {d.recent?.length > 0 && <div className="mt-4" data-testid="my-steps-recent"><div className="ax-label mb-2">Done today</div>
        <div className="grid gap-3 md:grid-cols-2">{d.recent.map((x) => <StepCard key={x.process_id} x={x} onChanged={load} hi={hi === x.process_id} today={today} />)}</div></div>}
    </div>
  );
};
